import { LocalizationService } from '@abp/ng.core';
import { ConnectedPosition, Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { EMPTY, Observable } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';

import { ZendraLocalizationServices } from '../enums/abpLocalizationServices';
import { localizeWithFallback } from '../utils/localization-utils';
import type { ZnSelectOption } from './zn-select.types';
import type { ZnSelectOptionsLoader } from './zn-select-loader';

export function normalizeZnSelectIds(value: string[] | string | null): string[] {
  if (Array.isArray(value)) {
    return value.map(v => String(v));
  }

  if (value === null || value === undefined || value === '') {
    return [];
  }

  return [String(value)];
}

export function resolveSinglePlaceholder(
  customPlaceholder: string,
  showPlaceholder: boolean,
  selectedLabel: string
): string {
  if (customPlaceholder) {
    return customPlaceholder;
  }

  if (!showPlaceholder && !selectedLabel) {
    return '';
  }

  return selectedLabel || 'ZnSelectPlaceholder';
}

export function resolveMultiPillsPlaceholder(
  customPlaceholder: string,
  showPlaceholder: boolean
): string {
  if (customPlaceholder) {
    return customPlaceholder;
  }

  if (!showPlaceholder) {
    return '';
  }

  return 'ZnSelectAndSearchPlaceholder';
}

export function createZnSelectOverlayConfig(
  overlay: Overlay,
  origin: HTMLElement,
  openUp: boolean,
  minWidth = 0
): OverlayConfig {
  const width = Math.max(origin.offsetWidth, minWidth);
  const grid = origin.closest('.slickgrid-container');
  const rightBoundary = Math.min(
    grid?.getBoundingClientRect().right ?? Number.POSITIVE_INFINITY,
    origin.ownerDocument.documentElement.clientWidth - 16
  );
  const alignEnd = origin.getBoundingClientRect().left + width > rightBoundary;
  const position: ConnectedPosition = openUp
    ? {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: -4,
      }
    : {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 4,
      };

  const positionStrategy = overlay
    .position()
    .flexibleConnectedTo(origin)
    .withPositions(alignEnd
      ? [{ ...position, originX: 'end', overlayX: 'end' }, position]
      : [position, { ...position, originX: 'end', overlayX: 'end' }])
    .withFlexibleDimensions(false)
    .withPush(true)
    .withViewportMargin(16);

  return new OverlayConfig({
    positionStrategy,
    hasBackdrop: false,
    panelClass: 'zn-select-overlay-pane',
    scrollStrategy: overlay.scrollStrategies.reposition(),
    width,
  });
}

export function buildZnSelectLoadOptionsErrorMessage(
  localizationService: LocalizationService,
  resource: ZendraLocalizationServices,
  errorEntityKey: string,
  error?: unknown
): string {
  const templateKey = `${resource}::ZnSelectLoadOptionsError`;
  const permissionTemplateKey = `${resource}::ZnSelectLoadOptionsPermissionError`;
  const template = localizeWithFallback(localizationService, templateKey);
  const permissionTemplate = localizeWithFallback(localizationService, permissionTemplateKey);
  const entityLabel = errorEntityKey ? localizeWithFallback(localizationService, errorEntityKey) : '';
  const status = typeof error === 'object' && error !== null && 'status' in error
    ? Number((error as { status?: number }).status)
    : undefined;
  const resolvedTemplate =
    status === 403 && permissionTemplate !== permissionTemplateKey
      ? permissionTemplate
      : template;

  if (resolvedTemplate && resolvedTemplate !== templateKey) {
    return resolvedTemplate.replace('{0}', entityLabel || '').trim();
  }

  return resolvedTemplate;
}

export function areZnSelectOptionsAllSelected(
  options: ZnSelectOption[],
  selectedIds: string[],
  requireNonEmpty: boolean = true
): boolean {
  if (requireNonEmpty && options.length === 0) {
    return false;
  }

  const selectedSet = new Set(selectedIds);
  return options.every(option => selectedSet.has(String(option.key)));
}

type ZnSelectFetchPage = {
  items: Array<{ key: string; value: string }>;
  total: number | null;
  skipCount: number;
};

export function fetchAllZnSelectOptions(
  loader: ZnSelectOptionsLoader,
  pageSize: number,
  searchTerm: string
): Observable<{ items: Array<{ key: string; value: string }>; total: number | null }> {
  const fetchPage = (skipCount: number) =>
    loader({ search: searchTerm, skipCount, maxResultCount: pageSize, forceRefresh: true }).pipe(
      map(response => {
        const items = Array.isArray(response) ? response : response.items;
        const total = Array.isArray(response) ? null : response.totalCount ?? null;
        return { items: items ?? [], total, skipCount } satisfies ZnSelectFetchPage;
      })
    );

  return fetchPage(0).pipe(
    expand(({ items, total, skipCount }) => {
      const nextSkip = skipCount + items.length;
      if (!items.length) {
        return EMPTY;
      }
      if (total !== null && nextSkip >= total) {
        return EMPTY;
      }
      return fetchPage(nextSkip);
    }),
    reduce(
      (acc, { items, total }) => ({
        items: acc.items.concat(items),
        total: acc.total ?? total,
      }),
      { items: [] as Array<{ key: string; value: string }>, total: null as number | null }
    )
  );
}
